(() => {
  const LEAFLET_CSS = 'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/leaflet.css';
  const LEAFLET_JS = [
    'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/leaflet.js',
    'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'
  ];

  const normalize = (value) =>
    (value || '')
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();

  const escapeHtml = (value) =>
    (value || '')
      .toString()
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const loadStyle = (href) => {
    if (document.querySelector(`link[href="${href}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  };

  const loadScript = (src) =>
    new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', reject, { once: true });
        if (window.L) resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });

  const loadLeaflet = async () => {
    if (window.L) return window.L;
    loadStyle(LEAFLET_CSS);

    for (const src of LEAFLET_JS) {
      try {
        await loadScript(src);
        if (window.L) return window.L;
      } catch (error) {
        // Try the next CDN.
      }
    }

    throw new Error('Leaflet non disponibile');
  };

  const loadStoreData = async (root) => {
    const inline = root.querySelector('[data-store-json]');
    if (inline?.textContent?.trim()) {
      return JSON.parse(inline.textContent);
    }

    if (window.PrimaveraStoreLocatorData) return window.PrimaveraStoreLocatorData;

    const url = root.dataset.storeDataUrl;
    if (!url) throw new Error('Dati punti vendita non configurati');

    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Dati punti vendita non disponibili: ${response.status}`);
    return response.json();
  };

  class PrimaveraStoreLocator {
    constructor(root) {
      this.root = root;
      this.mapEl = root.querySelector('[data-store-map]');
      this.input = root.querySelector('[data-store-search-input]');
      this.count = root.querySelector('[data-store-count]');
      this.empty = root.querySelector('[data-store-empty]');
      this.list = root.querySelector('[data-store-list]');
      this.markers = new Map();
      this.items = new Map();
      this.stores = [];
      this.onSearch = this.onSearch.bind(this);
      this.input?.addEventListener('input', this.onSearch);
      this.init();
    }

    async init() {
      try {
        const data = await loadStoreData(this.root);
        this.stores = Array.isArray(data.stores) ? data.stores.filter((store) => store.lat && store.lng) : [];
        this.renderList();
        this.applyFilter('');
      } catch (error) {
        console.error('[PrimaVera store locator data]', error);
        if (this.count) this.count.textContent = 'Punti vendita non disponibili';
        return;
      }

      try {
        this.L = await loadLeaflet();
        this.createMap();
        this.applyFilter(this.input?.value || '');
      } catch (error) {
        console.error('[PrimaVera store locator map]', error);
        if (this.mapEl) {
          this.mapEl.classList.add('is-unavailable');
          this.mapEl.innerHTML = '<div class="primavera-store-locator__map-loading">Mappa non disponibile. Usa la ricerca a destra.</div>';
        }
      }
    }

    createMap() {
      if (!this.L || !this.mapEl) return;

      const centerLat = Number.parseFloat(this.root.dataset.storeLat) || 42.55;
      const centerLng = Number.parseFloat(this.root.dataset.storeLng) || 12.65;
      const zoom = Number.parseInt(this.root.dataset.storeZoom, 10) || 6;

      this.mapEl.innerHTML = '';
      this.map = this.L.map(this.mapEl, {
        center: [centerLat, centerLng],
        zoom,
        scrollWheelZoom: false,
        zoomControl: true
      });

      this.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; OpenStreetMap contributors'
      }).addTo(this.map);

      const pinLogo = this.root.dataset.storePinLogo || '';
      const bounds = [];

      this.stores.forEach((store) => {
        const markerEl = `<span class="primavera-map-marker__inner">${pinLogo ? `<img src="${escapeHtml(pinLogo)}" alt="">` : '<span class="primavera-map-marker__fallback">PV</span>'}</span>`;
        const icon = this.L.divIcon({
          className: 'primavera-map-marker',
          html: markerEl,
          iconSize: [36, 46],
          iconAnchor: [18, 44],
          popupAnchor: [0, -42]
        });
        const marker = this.L.marker([store.lat, store.lng], { icon });
        marker.bindPopup(this.popupHtml(store));
        marker.on('click', () => this.setActive(store.code, true));
        marker.addTo(this.map);
        this.markers.set(store.code, marker);
        bounds.push([store.lat, store.lng]);
      });

      if (bounds.length) {
        this.map.fitBounds(bounds, { padding: [24, 24], maxZoom: zoom });
      }
    }

    renderList() {
      if (!this.list) return;

      this.list.innerHTML = this.stores.map((store) => `
        <div class="primavera-store-locator__store" role="button" tabindex="0" data-store-item data-store-code="${escapeHtml(store.code)}" data-store-search="${escapeHtml(this.searchString(store))}">
          <span class="primavera-store-locator__store-main">
            <span class="primavera-store-locator__store-heading">
              <span class="primavera-store-locator__store-name">${escapeHtml(store.name)}</span>
              <span class="primavera-store-locator__store-city">${escapeHtml(store.city)}${store.province ? ` · ${escapeHtml(store.province)}` : ''}</span>
            </span>
            <span class="primavera-store-locator__store-code">${escapeHtml(store.code)}</span>
          </span>
          ${store.address ? `<span class="primavera-store-locator__store-address">${escapeHtml(this.fullAddress(store))}</span>` : ''}
          <span class="primavera-store-locator__store-meta">
            ${store.phone ? `<a href="tel:${escapeHtml(store.phone.replace(/\s/g, ''))}">${escapeHtml(store.phone)}</a>` : ''}
            ${store.email ? `<a href="mailto:${escapeHtml(store.email)}">${escapeHtml(store.email)}</a>` : ''}
          </span>
        </div>
      `).join('');

      this.items = new Map(Array.from(this.list.querySelectorAll('[data-store-item]')).map((item) => [item.dataset.storeCode, item]));
      this.items.forEach((item, code) => {
        item.addEventListener('click', () => this.setActive(code, true, true));
        item.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          this.setActive(code, true, true);
        });
      });
    }

    popupHtml(store) {
      return `
        <strong>${escapeHtml(store.name)}</strong><br>
        ${escapeHtml(store.city)}${store.province ? ` · ${escapeHtml(store.province)}` : ''}<br>
        ${store.address ? escapeHtml(this.fullAddress(store)) : ''}
        ${store.phone ? `<br><a href="tel:${escapeHtml(store.phone.replace(/\s/g, ''))}">${escapeHtml(store.phone)}</a>` : ''}
      `;
    }

    fullAddress(store) {
      return [store.address, [store.postal_code, store.city, store.province].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    }

    searchString(store) {
      return [store.code, store.name, store.address, store.postal_code, store.city, store.province, store.phone, store.email].filter(Boolean).join(' ');
    }

    onSearch() {
      this.applyFilter(this.input?.value || '');
    }

    applyFilter(rawQuery) {
      const query = normalize(rawQuery);
      let visible = 0;
      const visibleBounds = [];

      this.stores.forEach((store) => {
        const match = !query || normalize(this.searchString(store)).includes(query);
        const marker = this.markers.get(store.code);
        const item = this.items.get(store.code);

        if (item) item.hidden = !match;
        if (marker && this.map) {
          if (match) {
            marker.addTo(this.map);
            visibleBounds.push([store.lat, store.lng]);
          } else {
            marker.removeFrom(this.map);
          }
        }
        if (match) visible += 1;
      });

      if (this.empty) this.empty.hidden = visible !== 0;
      this.updateCount(visible);

      if (this.map && visibleBounds.length && query) {
        this.map.fitBounds(visibleBounds, { padding: [26, 26], maxZoom: 10 });
      }
    }

    setActive(code, openPopup = false, panTo = false) {
      this.items.forEach((item) => item.classList.toggle('is-active', item.dataset.storeCode === code));
      this.markers.forEach((marker, markerCode) => {
        const el = marker.getElement();
        if (el) el.classList.toggle('is-active', markerCode === code);
      });

      const marker = this.markers.get(code);
      const item = this.items.get(code);
      if (item) item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      if (marker && this.map) {
        if (panTo) this.map.panTo(marker.getLatLng());
        if (openPopup) marker.openPopup();
      }
    }

    updateCount(value) {
      if (!this.count) return;
      this.count.textContent = value === 1 ? '1 punto vendita' : `${value} punti vendita`;
    }
  }

  const READY_VERSION = '5';

  const init = (scope = document) => {
    scope.querySelectorAll('[data-primavera-store-locator]').forEach((root) => {
      if (root.dataset.storeLocatorReadyVersion === READY_VERSION) return;
      root.dataset.storeLocatorReady = 'true';
      root.dataset.storeLocatorReadyVersion = READY_VERSION;
      new PrimaveraStoreLocator(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
